import React from "react";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Clock, Utensils, AlertCircle, Calendar, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { translations, cuisineTypes } from "@/lib/translations";

interface PreviewPanelProps {
  preferences?: {
    dietary: {
      vegan: boolean;
      vegetarian: boolean;
      glutenFree: boolean;
      none: boolean;
    };
    cookingTime: number;
    cuisines: string[];
    includedIngredients: string[];
    excludedIngredients: string[];
    duration: number;
    servings: number;
  };
}

const defaultPreferences = {
  dietary: {
    vegan: false,
    vegetarian: false,
    glutenFree: false,
    none: true,
  },
  cookingTime: 45,
  cuisines: ["Italian", "Japanese"],
  includedIngredients: ["tomatoes", "garlic"],
  excludedIngredients: ["mushrooms"],
  duration: 7,
  servings: 4,
};

export default function PreviewPanel({
  preferences = defaultPreferences,
}: PreviewPanelProps) {
  const getDietaryRestrictions = () => {
    const restrictions = Object.entries(preferences.dietary)
      .filter(([_, value]) => value)
      .map(([key]) => key);
    return restrictions.length ? restrictions : ["none"];
  };

  return (
    <Card className="h-full w-full bg-white p-6 flex flex-col">
      <h2 className="text-2xl font-semibold mb-6 text-right">
        {translations.common.preview}
      </h2>

      <ScrollArea className="flex-grow">
        <div className="space-y-6">
          {/* Dietary Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 justify-end">
              <h3 className="text-lg font-medium">
                {translations.mealPlanner.preview.dietaryPreferences}
              </h3>
              <Utensils className="h-5 w-5 text-primary" />
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              {getDietaryRestrictions().map((restriction) => (
                <Badge
                  key={restriction}
                  variant="secondary"
                  className="text-right"
                >
                  {
                    translations.mealPlanner.dietary[
                      restriction as keyof typeof translations.mealPlanner.dietary
                    ]
                  }
                </Badge>
              ))}
            </div>
          </div>

          {/* Cooking Time Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 justify-end">
              <h3 className="text-lg font-medium">
                {translations.mealPlanner.preview.cookingTime}
              </h3>
              <Clock className="h-5 w-5 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground text-right">
              {preferences.cookingTime} {translations.common.minutes}
            </p>
          </div>

          {/* Plan Settings */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 justify-end">
              <h3 className="text-lg font-medium">
                {translations.mealPlanner.planSettings.title}
              </h3>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  {preferences.duration}{" "}
                  {translations.mealPlanner.planSettings.days}
                </span>
                <span className="text-sm text-muted-foreground flex items-center gap-2">
                  {translations.mealPlanner.planSettings.duration}
                  <Calendar className="h-4 w-4" />
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  {preferences.servings}{" "}
                  {translations.mealPlanner.planSettings.servingsCount}
                </span>
                <span className="text-sm text-muted-foreground flex items-center gap-2">
                  {translations.mealPlanner.planSettings.servings}
                  <Users className="h-4 w-4" />
                </span>
              </div>
            </div>
          </div>

          {/* Cuisine Preferences */}
          <div className="space-y-3">
            <h3 className="text-lg font-medium text-right">
              {translations.mealPlanner.preview.preferredCuisines}
            </h3>
            <div className="flex flex-wrap gap-2 justify-end">
              {preferences.cuisines.map((cuisine) => (
                <Badge key={cuisine} variant="outline" className="text-right">
                  {cuisineTypes[cuisine as keyof typeof cuisineTypes]}
                </Badge>
              ))}
            </div>
          </div>

          {/* Ingredient Preferences */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-right">
              {translations.mealPlanner.preview.ingredients}
            </h3>

            {/* Included Ingredients */}
            {preferences.includedIngredients.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-green-600 text-right">
                  {translations.mealPlanner.preview.include}
                </p>
                <div className="flex flex-wrap gap-2 justify-end">
                  {preferences.includedIngredients.map((ingredient) => (
                    <Badge
                      key={ingredient}
                      variant="secondary"
                      className="text-right"
                    >
                      {ingredient}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Excluded Ingredients */}
            {preferences.excludedIngredients.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-red-600 text-right">
                  {translations.mealPlanner.preview.exclude}
                </p>
                <div className="flex flex-wrap gap-2 justify-end">
                  {preferences.excludedIngredients.map((ingredient) => (
                    <Badge
                      key={ingredient}
                      variant="destructive"
                      className="text-right"
                    >
                      {ingredient}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Tips Section */}
          <div className="mt-6 p-4 bg-muted rounded-lg">
            <div className="flex items-start gap-3 justify-end">
              <div className="text-right">
                <h4 className="font-medium mb-1">
                  {translations.mealPlanner.preview.tips}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {translations.mealPlanner.preview.tipsDescription}
                </p>
              </div>
              <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
            </div>
          </div>
        </div>
      </ScrollArea>
    </Card>
  );
}
